import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../constants/api.js";
import AppButton from "../ui/AppButton.jsx";
import ModalOrSheet from "../ui/ModalOrSheet.jsx";
import { Input } from "../forms/shared/FormAtoms.jsx";
import { showError, showSuccess } from "../../utils/toast.js";
import { successButtonLoadingClassName } from "../../design/tokens.js";

export default function PersoenlicherCodeModal({ open, onClose, user, onUpdated }) {
  const canSign = Boolean(user?.can_sign_maengel);
  const [hasCode, setHasCode] = useState(Boolean(user?.has_unterschrift_code));
  const [hasPng, setHasPng] = useState(Boolean(user?.has_unterschrift_png));
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ current: "", code: "" });
  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/profile/me/unterschrift-code");
      setHasCode(Boolean(data?.has_code));
      setHasPng(Boolean(data?.has_png));
    } catch {
      setHasCode(Boolean(user?.has_unterschrift_code));
      setHasPng(Boolean(user?.has_unterschrift_png));
    } finally {
      setLoading(false);
    }
  }, [open, user?.has_unterschrift_code, user?.has_unterschrift_png]);

  useEffect(() => {
    if (!open) return;
    setHasCode(Boolean(user?.has_unterschrift_code));
    setHasPng(Boolean(user?.has_unterschrift_png));
    setForm({ current: "", code: "" });
    setPreviewKey(Date.now());
    refresh();
  }, [open, user?.has_unterschrift_code, user?.has_unterschrift_png, refresh]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const code = String(form.code || "").replace(/\D/g, "");
    if (code.length !== 8) {
      showError("Bitte genau 8 Ziffern wählen.");
      return;
    }
    if (hasCode && !String(form.current || "").replace(/\D/g, "")) {
      showError("Bitte zuerst den bisherigen Code eingeben.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profile/me/unterschrift-code", {
        code,
        current_code: hasCode ? form.current : undefined,
      });
      showSuccess(hasCode ? "Code geändert" : "Code gespeichert");
      setForm({ current: "", code: "" });
      setHasCode(true);
      onUpdated?.();
    } catch (ex) {
      const err = ex?.response?.data?.error;
      if (err === "INVALID_CURRENT_CODE") showError("Bisheriger Code ist falsch.");
      else if (err === "INVALID_CODE") showError(ex?.response?.data?.message || "Ungültiger Code.");
      else showError("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const onPickPng = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okType = /^image\/(png|jpeg|jpg|webp)$/i.test(file.type);
    if (!okType) {
      e.target.value = "";
      return showError("Bitte PNG, JPG oder WebP wählen.");
    }
    const MAX = 5 * 1024 * 1024;
    if (file.size > MAX) {
      e.target.value = "";
      return showError("Bild zu gross (max. 5 MB).");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("unterschrift", file);
      await api.post("/profile/me/unterschrift-png", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showSuccess("Unterschrift-PNG gespeichert");
      setHasPng(true);
      setPreviewKey(Date.now());
      onUpdated?.();
    } catch (ex) {
      const err = ex?.response?.data?.error;
      if (err === "ROLE_NOT_ALLOWED") showError("Nur Admin, PL und GL können eine Unterschrift hinterlegen.");
      else showError("Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const removePng = async () => {
    setUploading(true);
    try {
      await api.delete("/profile/me/unterschrift-png");
      showSuccess("Unterschrift-PNG entfernt");
      setHasPng(false);
      setPreviewKey(Date.now());
      onUpdated?.();
    } catch {
      showError("Entfernen fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <ModalOrSheet
      open={open}
      onClose={onClose}
      title={canSign ? "Fachliche Unterschrift" : "Persönlicher Code"}
      maxWidth="sm"
      footer={
        <div className="flex flex-col max-md:gap-2 sm:flex-row sm:justify-end gap-2">
          <AppButton type="button" variant="secondary" onClick={onClose} className="max-md:min-h-[44px]">
            Schliessen
          </AppButton>
          <AppButton
            type="submit"
            form="profil-persoenlicher-code-form"
            variant="primary"
            disabled={saving || loading}
            className={`max-md:min-h-[44px] ${saving ? successButtonLoadingClassName : ""}`}
          >
            {saving ? "Speichern…" : hasCode ? "Code ändern" : "Code speichern"}
          </AppButton>
        </div>
      }
    >
      <p className="mb-4 text-sm text-muted">
        8-stelliger Zifferncode für fachliche Unterschriften (z. B. Mängelberichte).
        {" "}
        <span className="text-secondary">Getrennt von Login-Passwort und Passkey.</span>
        {hasCode ? " Du hast bereits einen Code." : " Noch keiner gesetzt."}
      </p>

      {loading ? (
        <p className="text-sm text-muted">Lade…</p>
      ) : (
        <form id="profil-persoenlicher-code-form" onSubmit={submit} className="space-y-3">
          {hasCode ? (
            <div>
              <label className="block text-xs mb-1 text-blue-200/70">Bisheriger Code</label>
              <Input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={8}
                value={form.current}
                onChange={(e) => setForm((f) => ({ ...f, current: e.target.value.replace(/\D/g, "") }))}
                placeholder="8 Ziffern"
              />
            </div>
          ) : null}
          <div>
            <label className="block text-xs mb-1 text-blue-200/70">
              {hasCode ? "Neuer Code" : "Code wählen (8 Ziffern)"}
            </label>
            <Input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, "") }))}
              placeholder="8 Ziffern"
            />
          </div>
        </form>
      )}

      {canSign ? (
        <div className="mt-5 space-y-2 border-t border-white/10 pt-4">
          <p className="text-sm text-blue-100/90">Unterschrift als PNG</p>
          <p className="text-xs text-muted">
            Wird bei Mängelberichten nach Code-Bestätigung sauber eingesetzt. Kein Zeichnen im Bericht nötig.
          </p>
          {hasPng ? (
            <div className="rounded-lg border border-white/10 bg-[#f8fafc] p-3 flex justify-center">
              <img
                src={`/api/profile/me/unterschrift-png?t=${previewKey}`}
                alt="Hinterlegte Unterschrift"
                className="max-h-24 object-contain"
              />
            </div>
          ) : (
            <p className="text-xs text-amber-200/80">Noch keine PNG hinterlegt.</p>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
            className="hidden"
            onChange={onPickPng}
          />
          <div className="flex flex-wrap gap-2">
            <AppButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? "…" : hasPng ? "PNG ersetzen" : "PNG hochladen"}
            </AppButton>
            {hasPng ? (
              <AppButton type="button" variant="ghost" size="sm" disabled={uploading} onClick={removePng}>
                Entfernen
              </AppButton>
            ) : null}
          </div>
        </div>
      ) : null}
    </ModalOrSheet>
  );
}
